import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Container, Flex } from '../../styles/components.js';
import { useTheme } from '../../context/ThemeContext.jsx';
import { navItems } from '../../constants/data.js';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: ${({ $isScrolled }) => ($isScrolled ? '0.6rem' : '1.4rem')} 0;
  background-color: ${({ $isScrolled, theme }) =>
    $isScrolled ? theme.colors.glass : 'transparent'};
  backdrop-filter: ${({ $isScrolled }) => ($isScrolled ? 'blur(24px)' : 'none')};
  -webkit-backdrop-filter: ${({ $isScrolled }) => ($isScrolled ? 'blur(24px)' : 'none')};
  border-bottom: ${({ $isScrolled, theme }) =>
    $isScrolled ? `1px solid ${theme.colors.border}` : '1px solid transparent'};
  box-shadow: ${({ $isScrolled, theme }) =>
    $isScrolled ? theme.shadows.card : 'none'};
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

const Logo = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.04em;
  text-transform: uppercase;

  a {
    color: ${({ theme }) => theme.colors.textDark};
    display: flex;
    align-items: center;
    gap: 2px;
    font-weight: 900;

    span {
      color: ${({ theme }) => theme.colors.primary};
      text-shadow: ${({ theme }) => theme.shadows.glowSm};
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;

  a {
    display: block;
    padding: 0.5rem 1rem;
    color: ${({ $active, theme }) =>
    $active ? theme.colors.textDark : theme.colors.textGray};
    font-weight: 600;
    font-size: 0.85rem;
    border-radius: ${({ theme }) => theme.borderRadius.full};
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    letter-spacing: 0.02em;

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.primary}0a;
    }
  }

  ${({ $active, theme }) => $active && `
    a {
      background: ${theme.colors.primary}10;
      color: ${theme.colors.primary};
    }
  `}
`;

const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 1.2rem;
  background: ${({ theme }) => theme.colors.gradient};
  color: #fff !important;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 700;
  font-size: 0.825rem;
  font-family: 'Space Grotesk', sans-serif;
  box-shadow: ${({ theme }) => theme.shadows.glow};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  letter-spacing: 0.02em;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glowLg};
    filter: brightness(1.08);
  }
`;

const ThemeToggle = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.textDark};
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}50;
    background: ${({ theme }) => theme.colors.primary}10;
    box-shadow: ${({ theme }) => theme.shadows.glowSm};
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 8px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textDark};
  transition: all 0.2s;
  z-index: 201;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}50;
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: min(75vw, 320px);
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.surface};
  padding: 5rem 2rem 2rem;
  z-index: 200;
  box-shadow: -20px 0 60px rgba(0,0,0,0.15);
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileNavItem = styled.a`
  display: block;
  padding: 0.9rem 1.2rem;
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textDark};
  font-weight: 500;
  font-size: 1.05rem;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ $active, theme }) => $active ? `${theme.colors.primary}12` : 'transparent'};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 150;
  backdrop-filter: blur(4px);
`;

const scrollToSection = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    window.scrollTo({ top: element.offsetTop - 80, behavior: 'smooth' });
  }
};

const Link = ({ to, children, onClick, ...rest }) => {
  const handleClick = (e) => {
    if (to.startsWith('#')) {
      e.preventDefault();
      scrollToSection(to.substring(1));
      if (onClick) onClick();
    }
  };
  return <RouterLink to={to} onClick={handleClick} {...rest}>{children}</RouterLink>;
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('home');
  const { themeMode, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = document.querySelectorAll('section[id]');
      let activeSection = 'home';
      sections.forEach((section) => {
        const sectionTop = section.offsetTop - 100;
        const sectionHeight = section.offsetHeight;
        if (window.scrollY >= sectionTop && window.scrollY < sectionTop + sectionHeight) {
          activeSection = section.getAttribute('id');
        }
      });
      setActiveNavItem(activeSection);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const handleMobileNavClick = (path) => {
    if (path.startsWith('#')) scrollToSection(path.substring(1));
    closeMobileMenu();
  };

  return (
    <>
      <HeaderWrapper $isScrolled={isScrolled}>
        <Container>
          <Flex justify="space-between" align="center">
            <Logo>
              <Link to="/">
                CODE<span>X</span>MOBILE
              </Link>
            </Logo>

            <Nav>
              {navItems.map((item) =>
                item.download ? (
                  <DownloadBtn key={item.name} href={item.path} download>
                    ⬇ {item.name}
                  </DownloadBtn>
                ) : (
                  <NavItem key={item.name} $active={activeNavItem === item.path.substring(1)}>
                    <Link to={item.path}>{item.name}</Link>
                  </NavItem>
                )
              )}

              <ThemeToggle
                onClick={toggleTheme}
                aria-label="Toggle theme"
                whileTap={{ scale: 0.9 }}
                whileHover={{ rotate: 15 }}
              >
                {themeMode === 'light' ? '🌙' : '☀️'}
              </ThemeToggle>
            </Nav>

            <Flex align="center" gap="0.75rem">
              <ThemeToggle
                onClick={toggleTheme}
                aria-label="Toggle theme"
                whileTap={{ scale: 0.9 }}
                style={{ display: 'none' }}
              >
                {themeMode === 'light' ? '🌙' : '☀️'}
              </ThemeToggle>
              <HamburgerButton
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Open menu"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  {isMobileMenuOpen ? (
                    <>
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </>
                  ) : (
                    <>
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </>
                  )}
                </svg>
              </HamburgerButton>
            </Flex>
          </Flex>
        </Container>
      </HeaderWrapper>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobileMenu}
            />
            <MobileMenu
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              {navItems.map((item) => (
                <MobileNavItem
                  key={item.name}
                  $active={activeNavItem === item.path.substring(1)}
                  onClick={() => handleMobileNavClick(item.path)}
                  href={item.download ? item.path : undefined}
                  download={item.download || undefined}
                >
                  {item.name}
                </MobileNavItem>
              ))}
              <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid' }}>
                <MobileNavItem onClick={toggleTheme} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {themeMode === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
                </MobileNavItem>
              </div>
            </MobileMenu>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;